import React, { useMemo } from 'react'

import { Box, Tooltip, useTheme } from '@mui/material'
import {
  gridRowTreeSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-pro'
import { ChevronsDownUpIcon, ChevronsUpDownIcon } from 'lucide-react'

/**
 * Expand / collapse EVERY group on the current page of a tree-data table, in one
 * click. VRG-231 review round 3 — the user asked for this and asked where it should
 * live; this is the answer, and the reasoning is worth keeping because "where" was
 * the actual question.
 *
 * ## Why the grouping column's HEADER and not the toolbar
 *
 * Three candidate homes, and the header wins on all three counts that matter:
 *
 * - **Scope.** The control acts on the tree column and nothing else. The toolbar
 *   band (Download / Views / Filter / Sort / Layout) is page-scoped chrome; a
 *   sixth button there would be the only one whose effect is confined to one
 *   column. In the header it sits directly above the very chevrons it operates.
 * - **Conditionality.** It renders ONLY when the page actually contains a group
 *   (`groupNodes.length` below). A toolbar button would have to either appear and
 *   disappear as you paginate — which is worse than useless, the toolbar is the
 *   one row people expect to be stable — or sit there permanently disabled on the
 *   ~90% of pages that have no groups, which §46 would then require a tooltip to
 *   explain a state that is neither a permission nor an in-flight mutation.
 * - **Bands.** The toolbar is 49px and the header is not (§29). Adding a control
 *   to the toolbar means matching that band; a header adornment has no such
 *   obligation, and `headerWithTerm` already establishes that this header carries
 *   adornments.
 *
 * ## Semantics: one button, not two
 *
 * The label follows the MAJORITY state — if anything is collapsed, the button
 * expands everything; only once every group is open does it become "collapse all".
 * A pair of buttons would spend twice the header width to express a binary, and the
 * "expand all" half would be dead the moment everything is already open.
 *
 * ## Scope is the current PAGE, deliberately
 *
 * The row tree only ever holds the rows the grid has (server-side pagination, 20 at
 * a time), so this cannot mean "every group in the account" and does not pretend to.
 * That is the honest scope for a control that lives in a column header of a paginated
 * table; a whole-dataset expansion would need every group's children fetched up front.
 */
export const ExpandAllGroupsToggle = () => {
  const theme = useTheme()
  const apiRef = useGridApiContext()
  const rowTree = useGridSelector(apiRef, gridRowTreeSelector)

  const groupNodes = useMemo(
    () =>
      Object.values(rowTree ?? {}).filter(
        (node: any) => node?.type === 'group' && (node.children?.length ?? 0) > 0,
      ) as any[],
    [rowTree],
  )

  // No groups on this page — render nothing at all rather than a disabled control
  // (see the "Conditionality" note above).
  if (!groupNodes.length) return null

  const anyCollapsed = groupNodes.some(node => !node.childrenExpanded)
  const Icon = anyCollapsed ? ChevronsUpDownIcon : ChevronsDownUpIcon
  const label = anyCollapsed ? 'Expand all' : 'Collapse all'

  return (
    // VRG-231 review round 3 — RIGHT-ALIGNED and LABELLED, both on the owner's
    // report that a bare chevron next to the title was "very small and hard to
    // notice". Two changes, and the second is the one that actually fixes it:
    //
    // - `ml: 'auto'` pushes it to the column's right edge. The header's title
    //   container is 413px wide and the label + info glyph occupy only ~106 of it,
    //   so there is ~300px of dead space here, and `disableColumnMenu` is set on
    //   this grid — nothing else claims the right edge. The one real cost is
    //   proximity: the chevrons this operates sit at the FAR LEFT of each row, so
    //   the control is now ~300px from every target it toggles.
    // - Which is exactly why it carries a TEXT LABEL rather than a bigger glyph.
    //   A lone chevron 300px from anything it visibly affects is a worse affordance
    //   than a lone chevron beside the title; the words are what make it findable
    //   and self-explanatory, and they are what a bigger icon could not have
    //   supplied. It renders as a text control rather than a `Button` because a
    //   40px `Button` (§60's floor) does not fit a header row.
    <Tooltip title={`${label} groups on this page`}>
      {/* The header cell is a sort/drag target, so the click must not bubble into
          the grid's own header handlers — otherwise expanding every group also
          re-sorts the column underneath it. */}
      <Box
        component="span"
        role="button"
        tabIndex={0}
        aria-label={`${label} groups`}
        onClick={(e: React.MouseEvent) => {
          e.stopPropagation()
          groupNodes.forEach(node =>
            apiRef.current.setRowChildrenExpansion(node.id, anyCollapsed),
          )
        }}
        onKeyDown={(e: React.KeyboardEvent) => {
          if (e.key !== 'Enter' && e.key !== ' ') return
          e.preventDefault()
          e.stopPropagation()
          groupNodes.forEach(node =>
            apiRef.current.setRowChildrenExpansion(node.id, anyCollapsed),
          )
        }}
        sx={{
          ml: 'auto',
          mr: 2,
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
          flexShrink: 0,
          cursor: 'pointer',
          userSelect: 'none',
          borderRadius: 1,
          px: 1,
          py: 0.5,
          color: theme.palette.text.secondary,
          // The header sets its own font; only the weight is relaxed so the control
          // does not compete with the column NAME beside it.
          fontWeight: 400,
          '&:hover, &:focus-visible': {
            color: theme.palette.text.primary,
            backgroundColor: theme.palette.action.hover,
          },
          '&:focus-visible': { outline: `2px solid ${theme.palette.brand[600]}`, outlineOffset: 1 },
        }}
      >
        <Icon size={14} strokeWidth={1.5} />
        {label}
      </Box>
    </Tooltip>
  )
}
